import httpClient from "../lib/httpClient";
import { auth } from '../lib/firebase';

export interface Episode {
  id: string;
  name: string;
  sequence: number;
}

export interface EpisodeUploadStatus {
  id: string;
  name: string;
  sequence: number;
  status: 'NO REEL' | 'UPLOADED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  subtitleStatus?: 'SUBTITLE_LIVE' | 'NO_SUBTITLE' | null;
}

export interface EpisodePreview {
  sequence: number;
  reelId: string;
  state: string;
  videoUrl: string | null;
  renditions: Record<string, string>;
  subtitleUrl: string | null;
  durationSeconds: number | null;
}

export interface ReelUploadJobRequest {
  reelUrl: string;
  showId: string;
  episodeId: string;
  sequence: number;
  createdBy: string;
}

export interface CreateEpisodeRequest {
  showId: string;
  name: string;
  sequence: number;
  releaseDate: number;
  thumbNailImageData?: any;
  createdBy?: string;
}

export interface CreatedEpisode {
  id: string;
  showId: string;
  name: string;
  sequence: number;
  releaseDate: number;
  status: string;
  createdAt: number;
  updatedAt: number;
}

export interface SubtitleUpdateRequest {
  showId: string;
  episodeId: string;
  sequence: number;
  file: string; // Base64 encoded .srt content
}

// Service for reel upload functionality (following studio pattern)
export const reelUploadService = {
  // Fetch episodes with upload status
  fetchEpisodesWithStatus: async (showId: string): Promise<{ episodes: EpisodeUploadStatus[], showName: string }> => {
    try {
      console.log('Fetching episodes for showId:', showId);
      
      // Wait for authentication state to be restored
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
      });
      
      if (!auth.currentUser) {
        throw new Error('User not authenticated');
      }
      
      // Fetch show data with episodes (POST request like studio)
      const showResponse = await httpClient.post(`/studio/show/${showId}`, {
        requestedMetadata: [],
        includeEpisodes: true,
        includeEpisodeUserMapping: true
      },{
        resourceId: showId
      });

      // Fetch episode upload status
      const statusResponse = await httpClient.get(`/api/v1/reels-cms/episode-status/${showId}`);

      // Create status and subtitle status maps
      const statusMap = statusResponse.reduce((map: any, status: any) => {
        map[status.sequence] = {
          status: status.status,
          subtitleStatus: status.subtitleStatus
        };
        return map;
      }, {});

      // Transform episodes with status
      const episodesWithStatus: EpisodeUploadStatus[] = showResponse.episodes.map((episode: Episode) => ({
        id: episode.id,
        name: episode.name,
        sequence: episode.sequence,
        status: statusMap[episode.sequence]?.status || 'NO REEL',
        subtitleStatus: statusMap[episode.sequence]?.subtitleStatus || null
      }));

      return {
        episodes: episodesWithStatus,
        showName: showResponse.title || 'Untitled Show'
      };
    } catch (error) {
      console.error('Error fetching episodes with status:', error);
      throw error;
    }
  },

  async getEpisodePreviews(showId: string): Promise<EpisodePreview[]> {
    try {
      return await httpClient.get(`/api/v1/reels-cms/episode-preview/${showId}`);
    } catch (error) {
      console.error('Unable to fetch episode previews');
      throw error;
    }
  },

  // Create regular episode (sequence > 0)
  createEpisode: async (episodeData: CreateEpisodeRequest): Promise<CreatedEpisode> => {
    try {
      return await httpClient.post('/studio/episode', episodeData, {
        resourceId: episodeData.showId
      });
    } catch (error) {
      console.error('Error creating episode:', error);
      throw error;
    }
  },

  // Create trailer episode (sequence = 0)
  createTrailerEpisode: async (episodeData: CreateEpisodeRequest): Promise<CreatedEpisode> => {
    try {
      return await httpClient.post('/studio/episode/create-trailer-episode', episodeData, {
        resourceId: episodeData.showId
      });
    } catch (error) {
      console.error('Error creating trailer episode:', error);
      throw error;
    }
  },

  // Get generated URL for reel upload
  getGeneratedUrlForReel: async (fileName: string): Promise<{ signedUrl: string }> => {
    try {
      return await httpClient.post('/api/v1/reels-cms/generate-signed-url', { fileName });
    } catch (error) {
      console.error('Error getting generated URL:', error);
      throw error;
    }
  },

  // Create upload job
  createUploadJob: async (jobData: ReelUploadJobRequest): Promise<{ jobId: string }> => {
    try {
      console.log("creating upload job")
      return await httpClient.post('/api/v1/reels-cms/upload-job/create', jobData);
    } catch (error) {
      console.error('Error creating upload job:', error);
      throw error;
    }
  },

  // Upload file to generated URL
  uploadFileToGeneratedUrl: async (
    signedUrl: string,
    file: File,
    headers: Record<string, string>,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = (event.loaded / event.total) * 100;
            onProgress(progress);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      xhr.open('PUT', signedUrl);
      
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.send(file);
    });
  },

  // Upload subtitle for episode
  uploadSubtitle: async (subtitleData: SubtitleUpdateRequest): Promise<void> => {
    try {
      await httpClient.post('/api/v1/reels-cms/upload-subtitle', subtitleData, {
        resourceId: subtitleData.showId
      });
    } catch (error) {
      console.error('Error uploading subtitle:', error);
      throw error;
    }
  },

  // Delete episodes (mark as inactive)
  deleteEpisodes: async (showId: string, episodeIds: string[]): Promise<{ success: boolean; affectedEpisodes: number }> => {
    try {
      console.log('Marking episodes as inactive:', episodeIds);
      const response = await httpClient.delete(`/studio/episode/soft-delete`, {
        episodeIds
      }, {
        resourceId: showId
      });
      return response;
    } catch (error) {
      console.error('Error deleting episodes:', error);
      throw error;
    }
  },

  // Fetch inactive (deleted) episodes
  fetchInactiveEpisodes: async (showId: string): Promise<EpisodeUploadStatus[]> => {
    try {
      console.log('Fetching inactive episodes for showId:', showId);

      // Fetch show data with DELETED state episodes
      const showResponse = await httpClient.post(`/studio/show/${showId}`, {
        requestedMetadata: [],
        includeEpisodes: true,
        includeEpisodeUserMapping: true,
        states: ['DELETED'] // Request only deleted episodes
      }, {
        resourceId: showId
      });

      // Transform episodes - extract deletion info from metadata
      const inactiveEpisodes: EpisodeUploadStatus[] = (showResponse.episodes || []).map((episode: any) => {
        // Helper function to get metadata value by type
        const getMetadataValue = (type: string): string | undefined => {
          const metadata = (episode.metadata || []).find((m: any) => m.type === type);
          return metadata?.value;
        };

        const deletedAt = getMetadataValue('DELETED_AT');
        const deletedBy = getMetadataValue('DELETED_BY');
        const originalSequence = getMetadataValue('ORIGINAL_SEQUENCE');

        return {
          id: episode.id,
          name: episode.name,
          sequence: episode.sequence,
          originalSequence: originalSequence ? parseInt(originalSequence) : episode.sequence,
          deletedAt: deletedAt,
          deletedBy: deletedBy,
          status: 'DELETED' as any
        };
      });

      return inactiveEpisodes;
    } catch (error) {
      console.error('Error fetching inactive episodes:', error);
      throw error;
    }
  },
};
